import React from 'react';
import EDNumericEdit from '../../../../../../BaseComponents/NumericEdit/EDNumericEdit';
import { isNumericType } from '../../../SavedUrval/Utils/convertSavedUrval';
import { IClientAttributeModel } from '../../../../../../Types/ClientModelTypes';
import KontodelEdit from '../../../KontodelEdit/KontodelEdit';
import debounce from 'debounce';
import Logger from '../../../../../../Utils/Logger';
import EDDatePicker from '../../../../../../BaseComponents/DatePicker/EDDatePicker';
import { OnChange } from '../../../../../Attributes/EditableAttribute/EditComponentTypes';
import { EDTextEdit } from '../../../../../../BaseComponents/TextEdit/EDTextEdit';

interface IProps {
  id: string;
  label: string;
  value?: string | number;
  onChange: (newValue: string | number | null) => void;
  attribute: IClientAttributeModel;
  disabled?: boolean;
  className?: string;
}

interface IState {
  value?: string | number;
  localValue?: string | number;
}

class ConditionValueEdit extends React.Component<IProps, IState> {
  static getDerivedStateFromProps(props: IProps, state: IState) {
    if (props.value !== state.value) {
      return {
        value: props.value,
        prevValue: props.value,
      };
    }

    return null;
  }

  debouncedOnChange: ((newValue: string | number | null) => void) & {
    flush: () => void;
    clear: () => void;
  };

  constructor(props: IProps) {
    super(props);

    // Use a stable debounced function that always calls the latest props.onChange
    // via `this`, so we never need to recreate it when the prop reference changes.
    this.debouncedOnChange = debounce(
      (newValue: string | number | null) => {
        this.props.onChange(newValue);
      },
      500
    );
    this.state = { localValue: props.value, value: props.value };
  }

  componentWillUnmount() {
    this.debouncedOnChange.clear();
  }

  /** Flush any pending debounced value so it propagates immediately on blur. */
  onBlur = () => {
    this.debouncedOnChange.flush();
  };

  /** Flush any pending debounced value when Enter is pressed, so the value
   *  is submitted even when a global Enter keybind triggers before blur. */
  onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      this.debouncedOnChange.flush();
    }
  };

  onKontoChange = (newValue: string | number | null, callback?: () => void) => {
    const value = newValue == null ? undefined : newValue;
    this.setState({ localValue: value }, callback);
    this.debouncedOnChange(newValue);
  };

  onChange: OnChange<string | number> = (newValue: string | number | null) => {
    const value = newValue == null ? undefined : newValue;
    this.setState({ localValue: value });
    this.debouncedOnChange(newValue);
  };
  render() {
    const { attribute, className, id, label, disabled } = this.props;
    const { localValue } = this.state;
    const testId = `ConditionValueEdit-${attribute.AttributeName}`;

    // TODO: Show suitable edit components
    if (isNumericType(attribute.DataType)) {
      if (typeof localValue === 'number' || localValue == null) {
        return (
          <EDNumericEdit
            id={id}
            label={label}
            value={localValue}
            onChange={this.props.onChange}
            disabled={disabled}
            className={className}
            testId={testId}
          />
        );
      } else {
        Logger.error(
          `Selectionfield ${attribute.DisplayLabel} is of type Int or float but value is not of type number`
        );
        return <div>Error see console</div>;
      }
    } else if (
      attribute.DataType === 'String' 
    ) {
      if (typeof localValue === 'string' || localValue == null) {
        if (
          attribute.AttributeName.startsWith('KONTODEL_') ||
          attribute.AttributeName.startsWith('Konto_del')
        ) {
          return (
            <KontodelEdit
              id={id}
              label={label}
              value={localValue || ''}
              onChange={this.onKontoChange}
              disabled={disabled}
              className={className}
              attribute={attribute}
              testId={testId}
            />
          );
        } else {
          return (
            <EDTextEdit
              id={id}
              label={label}
              value={localValue || ''}
              onChange={this.onChange}
              onBlur={this.onBlur}
              onKeyDown={this.onKeyDown}
              disabled={disabled}
              allMargins="none"
              fullWidth
              testId={testId}
            />
          );
        }
      } else {
        Logger.error(
          `Selectionfield ${attribute.DisplayLabel} is of type String but value is not of type string`
        );
        return <div>Error see console</div>;
      }
    } else if (attribute.DataType === 'Date') {
      if (typeof localValue === 'string' || localValue == null) {
        return (
          <EDDatePicker
            id={id}
            label={`${label}`}
            value={localValue || ''}
            onChange={newValue => this.onChange(newValue)}
            onBlur={this.onBlur}
            onKeyDown={this.onKeyDown}
            disabled={disabled}
            className={className}
            allMargins="none"
            fullWidth
            testId={testId}
          />
        );
      } else {
        Logger.error(
          `Selectionfield ${attribute.DisplayLabel} is of type Date but value is not of type string`
        );
        return <div>Error see console</div>;
      }
    } else if (
      attribute.DataType === 'Time' ||
      attribute.DataType === 'DateTime'
    ) {
      if (typeof localValue === 'string' || localValue == null) {
        // TODO: Render time/datetime component if DataType === 'Time'/'DateTime'
        return (
          <EDTextEdit
            id={id}
            label={`${label}`}
            value={localValue || ''}
            onChange={this.onChange}
            onBlur={this.onBlur}
            onKeyDown={this.onKeyDown}
            disabled={disabled}
            allMargins="none"
            fullWidth
            testId={testId}
          />
        );
      } else {
        Logger.error(
          `Selectionfield ${attribute.DisplayLabel} is of type Date but value is not of type string`
        );
        return <div>Error see console</div>;
      }
    } else {
      Logger.error(
        `Could not find suitable edit component for field: ${attribute.AttributeName} with value: ${localValue} and DataType: ${attribute.DataType}`
      );
      return <div>Error</div>;
    }
  }
}

export default ConditionValueEdit;
